<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import {
    InboundEmailsService,
    type SuperUserInboundEmail as InboundEmail,
    type CreateCaseFromInboundEmailRequest
  } from '@/types/generated'
  import { useRoute } from 'vue-router'
  import { ROUTES } from '@/router/routes'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import TextAreaField from '@/components/admin/form/TextAreaField.vue'
  import { Form } from 'vee-validate'
  import * as yup from 'yup'
  import DownloadIcon from '@/components/icons/DownloadIcon.vue'
  import { FileIcon } from 'lucide-vue-next'

  const email = ref<InboundEmail>()
  const data = ref<CreateCaseFromInboundEmailRequest>({})
  const activeTab = ref<'text' | 'html'>('text') // Track which tab is active

  const submitCase = async (values: CreateCaseFromInboundEmailRequest) => {
    await InboundEmailsService.inboundEmailsCreateCaseCreate({
      uuid: email.value?.uuid,
      requestBody: values
    })
  }

  onMounted(async () => {
    const uuid = useRoute().params.uuid
    email.value = await InboundEmailsService.inboundEmailsRetrieve({ uuid })
  })
</script>

<template>
  <div v-if="email">
    <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
      <!-- Page Header -->
      <div class="flex items-center justify-between gap-x-5">
        <Breadcrumbs
          :items="[
            { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
            { title: 'Emails', to: ROUTES.ADMIN.EMAIL.INBOUND_EMAIL_LIST },
            {
              title: `Details - ${email.uuid}`,
              to: ROUTES.ADMIN.EMAIL.INBOUND_EMAIL_DETAIL.replace(':uuid', email.uuid)
            }
          ]"
        />
        <div class="flex items-center justify-end gap-x-2"></div>
      </div>
      <div
        class="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div class="w-full">
          <div class="flex gap-x-4">
            <!-- Left Column (Text) -->
            <div class="w-1/2 overflow-hidden p-4">
              <!-- Tabs Navigation -->
              <div class="mb-4 flex border-b">
                <button
                  @click="activeTab = 'text'"
                  :class="[
                    'px-4 py-2',
                    activeTab === 'text'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-blue-600'
                  ]"
                >
                  Plain Text
                </button>
                <button
                  @click="activeTab = 'html'"
                  :class="[
                    'ml-4 px-4 py-2',
                    activeTab === 'html'
                      ? 'border-b-2 border-blue-600 text-blue-600'
                      : 'text-gray-600 hover:text-blue-600'
                  ]"
                >
                  HTML
                </button>
              </div>

              <!-- Tab Content -->
              <div
                v-if="activeTab === 'text'"
                class="max-h-96 overflow-auto whitespace-pre-wrap break-words"
              >
                {{ email.text }}
              </div>
              <div v-if="activeTab === 'html'">
                <!-- Render HTML content inside an iframe -->
                <iframe
                  :srcdoc="email.html"
                  class="h-96 w-full border border-gray-300"
                ></iframe>
              </div>
              <div class="space-y-4 pb-4">
                <label class="mb-2 block text-sm font-medium">Attachments</label>
                <div
                  v-for="attachment in email.attachments"
                  :key="attachment.uuid"
                  class="flex items-center justify-between gap-x-4 rounded-lg border border-gray-200 p-3 shadow-sm
                    dark:border-neutral-700 dark:bg-neutral-800"
                >
                  <div class="flex items-center gap-3">
                    <FileIcon class="h-6 w-6 text-gray-500" />
                    <div>
                      <p class="text-sm font-medium text-gray-800 dark:text-gray-300">
                        {{ attachment.file_name }}
                      </p>
                      <p class="text-xs text-gray-500 dark:text-gray-400"></p>
                    </div>
                  </div>
                  <div>
                    <a
                      :href="attachment.file"
                      target="_blank"
                      class="flex items-center gap-x-2 text-sm font-medium text-blue-600 hover:underline"
                    >
                      <DownloadIcon class="h-5 w-5" />
                      Download
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <!-- Right Column (Form) -->
            <div class="w-1/2 p-4">
              <!-- Right Column (Form) -->
              <Form @submit="submitCase">
                <TextAreaField
                  name="inquiry"
                  v-model="data.inquiry"
                  label="Inquiry"
                  :rules="yup.string().required()"
                />
                <SelectField
                  :options="[
                    { value: 'Herr', title: 'Herr' },
                    { value: 'Frau', title: 'Frau' }
                  ]"
                  name="client_title"
                  v-model="data.client_title"
                  label="Client Title"
                  :rules="yup.string().required()"
                />
                <div class="flex gap-4">
                  <div class="w-1/2">
                    <InputField
                      name="client_first_name"
                      v-model="data.client_first_name"
                      label="Client First Name"
                      :rules="yup.string().notRequired()"
                    />
                  </div>
                  <div class="w-1/2">
                    <InputField
                      name="client_last_name"
                      v-model="data.client_last_name"
                      label="Client Last Name"
                      :rules="yup.string().required()"
                    />
                  </div>
                </div>
                <InputField
                  name="client_email"
                  v-model="data.client_email"
                  label="Client Email"
                  :rules="yup.string().email().required()"
                />
                <InputField
                  name="client_phone"
                  v-model="data.client_phone"
                  label="Client Phone Number"
                  :rules="yup.string().optional()"
                />
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
